/**
 * <lead-form> — Attaquer storefront lead form web component.
 *
 * Submits multipart/form-data to the App Proxy endpoint (defaults `/apps/forms`)
 * which forwards to the Cloudflare Worker. Worker handles HMAC verify, Turnstile
 * verification, validation, R2 audit log, Shopify Files upload, and Klaviyo push.
 *
 * Honeypot + 2s time guard reject obvious bots client-side as a first filter.
 * Turnstile (invisible) catches the rest before the Worker runs.
 */
(function () {
  'use strict';
  if (customElements.get('lead-form')) return;

  var MIN_SUBMIT_MS = 2000;

  class LeadForm extends HTMLElement {
    constructor() { super(); }
  }

  LeadForm.prototype.connectedCallback = function () {
    this.endpoint = this.dataset.endpoint || '/apps/forms';
    this.formType = this.dataset.formType || '';
    this.formName = this.dataset.formName || ('attaquer_' + this.formType + '_v1');
    this.turnstileSiteKey = this.dataset.turnstileSiteKey || '';

    this.form = this.querySelector('form');
    this.successEl = this.querySelector('[data-form-success]');
    this.statusEl = this.querySelector('[data-form-status]');
    this.submitBtn = this.querySelector('[data-submit]');
    this.submitIdle = this.querySelector('[data-submit-idle]');
    this.submitLoading = this.querySelector('[data-submit-loading]');
    this.honeypot = this.querySelector('[name="leave_empty"]');

    if (!this.form) return;

    this._firstFocusAt = 0;
    this._submitting = false;

    this.form.addEventListener('focusin', this._onFirstFocus.bind(this), { once: true });
    this.form.addEventListener('submit', this._onSubmit.bind(this));
    this.form.addEventListener('input', this._clearFieldError.bind(this));
    this.form.addEventListener('change', this._onFileChange.bind(this));
  };

  LeadForm.prototype._onFirstFocus = function () {
    this._firstFocusAt = Date.now();
  };

  LeadForm.prototype._onFileChange = function (e) {
    var t = e.target;
    if (!(t instanceof HTMLInputElement) || t.type !== 'file') return;
    var zone = t.closest('[data-dropzone]');
    if (!zone) return;
    var placeholder = zone.querySelector('[data-dropzone-placeholder]');
    var filesEl = zone.querySelector('[data-dropzone-files]');
    if (!t.files || !t.files.length) {
      if (placeholder) placeholder.hidden = false;
      if (filesEl) { filesEl.hidden = true; filesEl.textContent = ''; }
      return;
    }
    var names = Array.prototype.map.call(t.files, function (f) { return f.name; }).join(', ');
    if (placeholder) placeholder.hidden = true;
    if (filesEl) { filesEl.hidden = false; filesEl.textContent = names; }
  };

  LeadForm.prototype._setStatus = function (msg, kind) {
    if (!this.statusEl) return;
    this.statusEl.textContent = msg || '';
    this.statusEl.dataset.kind = kind || '';
  };

  LeadForm.prototype._clearFieldError = function (e) {
    var t = e.target;
    if (!t || !t.name) return;
    if (t.getAttribute('aria-invalid') === 'true') {
      t.setAttribute('aria-invalid', 'false');
      var err = this.form.querySelector('[data-field-error-for="' + t.name + '"]');
      if (err) err.remove();
    }
  };

  LeadForm.prototype._fieldError = function (name, message) {
    var input = this.form.querySelector('[name="' + name + '"]');
    if (!input) return;
    input.setAttribute('aria-invalid', 'true');
    var err = document.createElement('div');
    err.className = 'lead-form__field-error';
    err.setAttribute('data-field-error-for', name);
    err.textContent = message;
    var parent = input.closest('.lead-form__field') || input.parentNode;
    parent.appendChild(err);
  };

  LeadForm.prototype._setSubmitting = function (on) {
    this._submitting = !!on;
    if (this.submitBtn) this.submitBtn.disabled = !!on;
    if (this.submitIdle) this.submitIdle.hidden = !!on;
    if (this.submitLoading) this.submitLoading.hidden = !on;
  };

  LeadForm.prototype._validate = function (fd) {
    var ok = true;
    var requiredInputs = this.form.querySelectorAll('[required]');
    for (var i = 0; i < requiredInputs.length; i++) {
      var el = requiredInputs[i];
      var name = el.getAttribute('name');
      if (!name) continue;
      var v;
      if (el.type === 'file') {
        v = el.files && el.files.length ? 'has' : '';
      } else if (el.type === 'checkbox') {
        v = el.checked ? 'on' : '';
      } else {
        v = (fd.get(name) || '').toString().trim();
      }
      if (!v) {
        this._fieldError(name, 'Required.');
        ok = false;
      }
    }
    var email = (fd.get('email') || '').toString().trim();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this._fieldError('email', 'Please enter a valid email address.');
      ok = false;
    }
    if (!this._validateKitQty(fd)) ok = false;
    return ok;
  };

  LeadForm.prototype._validateKitQty = function (fd) {
    var group = this.form.querySelector('[data-kit-qty-group]');
    if (!group) return true;
    var inputs = group.querySelectorAll('.lead-form__kit-qty-input');
    if (!inputs.length) return true;
    var stale = group.querySelectorAll('.lead-form__fieldset-error');
    for (var s = 0; s < stale.length; s++) stale[s].remove();
    var parts = [];
    var ok = true;
    for (var i = 0; i < inputs.length; i++) {
      var el = inputs[i];
      var raw = (el.value || '').trim();
      var n = raw === '' ? 0 : parseInt(raw, 10);
      if (isNaN(n) || n < 0) n = 0;
      if (n > 0 && n < 31) {
        el.setAttribute('aria-invalid', 'true');
        var fieldErr = document.createElement('div');
        fieldErr.className = 'lead-form__field-error';
        fieldErr.textContent = 'Minimum 31 pcs per style (or 0 to skip).';
        el.parentNode.appendChild(fieldErr);
        ok = false;
        continue;
      }
      if (n > 0) {
        var style = el.getAttribute('data-kit-style') || '';
        parts.push(style + ': ' + n);
      }
    }
    if (ok && !parts.length) {
      var groupErr = document.createElement('div');
      groupErr.className = 'lead-form__fieldset-error';
      groupErr.textContent = 'Enter a quantity (31 or more) for at least one style.';
      group.appendChild(groupErr);
      ok = false;
    }
    if (ok) {
      var hidden = this.form.querySelector('input[type="hidden"][name="kit_needed"]');
      if (hidden) {
        hidden.value = parts.join('; ');
        fd.set('kit_needed', hidden.value);
      }
    }
    return ok;
  };

  LeadForm.prototype._getTurnstileToken = function () {
    if (!this.turnstileSiteKey || !window.turnstile) return Promise.resolve('');
    var self = this;
    return new Promise(function (resolve) {
      var widget = self.querySelector('.cf-turnstile');
      if (!widget) return resolve('');
      try {
        window.turnstile.execute(widget, {
          callback: function (token) { resolve(token); },
          'error-callback': function () { resolve(''); },
        });
      } catch (e) {
        resolve('');
      }
      // 8s timeout fallback
      setTimeout(function () { resolve(''); }, 8000);
    });
  };

  LeadForm.prototype._showCoupon = function (body) {
    if (!body || !body.discount_code) return;
    var wrap = this.querySelector('[data-form-coupon]');
    var code = this.querySelector('[data-coupon-code]');
    var cta = this.querySelector('[data-coupon-cta]');
    if (!wrap || !code) return;
    code.textContent = body.discount_code;
    if (cta && body.discount_url) cta.setAttribute('href', body.discount_url);
    wrap.hidden = false;

    var copy = this.querySelector('[data-coupon-copy]');
    if (copy && !copy._bound) {
      copy._bound = true;
      copy.addEventListener('click', function () {
        var done = function () {
          var was = copy.textContent;
          copy.textContent = 'Copied';
          copy.setAttribute('data-copied', '');
          setTimeout(function () { copy.textContent = was; copy.removeAttribute('data-copied'); }, 2000);
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(code.textContent).then(done, function () {});
          return;
        }
        try {
          var r = document.createRange();
          r.selectNodeContents(code);
          var sel = window.getSelection();
          sel.removeAllRanges();
          sel.addRange(r);
          document.execCommand('copy');
          sel.removeAllRanges();
          done();
        } catch (e) {}
      });
    }
  };

  LeadForm.prototype._showSuccess = function (body) {
    this._showCoupon(body);
    this.setAttribute('data-success', '');
    if (this.form) this.form.hidden = true;
    if (this.successEl) this.successEl.hidden = false;
    this._setStatus('');
    try { this.successEl && this.successEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); } catch (e) {}
  };

  LeadForm.prototype._onSubmit = function (e) {
    e.preventDefault();
    if (this._submitting) return;
    if (this.honeypot && this.honeypot.value) return;

    // Clear previous errors
    var prior = this.form.querySelectorAll('.lead-form__field-error');
    for (var p = 0; p < prior.length; p++) prior[p].remove();
    var invalids = this.form.querySelectorAll('[aria-invalid="true"]');
    for (var q = 0; q < invalids.length; q++) invalids[q].setAttribute('aria-invalid', 'false');

    var fd = new FormData(this.form);
    if (!this._validate(fd)) {
      this._setStatus('Please correct the errors above.', 'error');
      var firstErr = this.form.querySelector('[aria-invalid="true"]');
      if (firstErr) try { firstErr.focus(); } catch (e2) {}
      return;
    }

    var elapsed = this._firstFocusAt ? (Date.now() - this._firstFocusAt) : 0;
    if (elapsed > 0 && elapsed < MIN_SUBMIT_MS) return;

    var self = this;
    this._setSubmitting(true);
    this._setStatus('');

    this._getTurnstileToken().then(function (token) {
      fd.set('cf_turnstile_response', token || '');
      return fetch(self.endpoint, { method: 'POST', body: fd, credentials: 'same-origin' });
    }).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (body) {
        return { status: res.status, body: body };
      });
    }).then(function (r) {
      if (r.status === 200 && r.body && r.body.ok) {
        self._showSuccess(r.body);
        return;
      }
      if (r.status === 400 && r.body && r.body.fields) {
        Object.keys(r.body.fields).forEach(function (name) {
          self._fieldError(name, friendlyError(r.body.fields[name]));
        });
        self._setStatus('Please correct the errors above.', 'error');
        return;
      }
      if (r.status === 429) {
        self._setStatus("Too many submissions from this network. Please try again in a minute.", 'error');
        return;
      }
      if (r.status === 401) {
        self._setStatus('Session expired — please refresh the page and try again.', 'error');
        return;
      }
      var detail = (r.body && (r.body.error || r.body.detail)) || ('http_' + r.status);
      self._setStatus('We couldn\'t submit your form (' + detail + '). Please try again or email info@attaquercycling.com.', 'error');
    }).catch(function () {
      self._setStatus('Network error. Please try again or email info@attaquercycling.com.', 'error');
    }).finally(function () {
      self._setSubmitting(false);
    });
  };

  function friendlyError(code) {
    switch (code) {
      case 'required': return 'Required.';
      case 'invalid_email': return 'Please enter a valid email address.';
      default: return code || 'Please check this field.';
    }
  }

  customElements.define('lead-form', LeadForm);
})();
